// ./src/utils/api/TaxSettingService.ts
//
// Account-level reusable tax rates ("Standard VAT 20%", "Reduced 5%", "Zero-rated"), referenced by
// an event (`event.taxSettingId`) and, per ticket type, by `ticketType.taxSettingId`.
//
// ⚠⚠ `isInclusive` IS THE FIELD THAT DECIDES WHAT A GUEST READS. An exclusive rate is ADDED to the
// price; an inclusive one is already inside it. The server has always computed both correctly, but
// every receipt rendered a single "Tax" row inside a running sum — so an inclusive rate produced
// "Subtotal 100, Tax 16.67, Total 100". Read `taxAdded` alongside `tax` on an order's pricing
// (ROADMAP A2b #52) rather than assuming the whole amount was added.
import { gql } from '@apollo/client';
import { OpResult, OP_RESULT_CODES } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import { Pagination, SortItem } from './commonApiTypes';
import { runBulkWrites } from "../bulkWrites";

const FIELDS = `
  id
  accountId
  name
  rate
  isInclusive
  isDefault
  region
  description
  status
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query TaxSettingList($filter: TaxSettingFilter, $params: PaginationAndSorting) {
    taxSettingList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query TaxSettingGet($id: ID) {
    taxSettingGet(id: $id) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation TaxSettingCreate($params: TaxSettingInput) {
    taxSettingCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation TaxSettingUpdate($where: TaxSettingWhereInput, $params: TaxSettingInput) {
    taxSettingUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation TaxSettingRemove($where: TaxSettingWhereInput) {
    taxSettingRemove(where: $where) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export interface TaxSetting {
  id?: string;
  accountId?: string | null;
  name?: string | null;
  /** Percentage, e.g. 20 = 20%. */
  rate?: number | null;
  /** True when the price already contains the tax; false when it is added on top. */
  isInclusive?: boolean | null;
  isDefault?: boolean | null;
  region?: string | null;
  description?: string | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface TaxSettingInput {
  name?: string | null;
  rate?: number | null;
  isInclusive?: boolean | null;
  isDefault?: boolean | null;
  region?: string | null;
  description?: string | null;
}

export interface TaxSettingFilter {
  id?: string[];
  searchKeyword?: string;
}

export interface TaxSettingWhereInput {
  id?: string;
}

export class TaxSettingService {
  /**
   * ⚠ Every argument is optional, so the existing `TaxSettingService.list()` call from the event's
   * tax picker keeps working unchanged. A signature change here would have silently emptied that
   * picker — it reads the same catalog this page now edits.
   */
  static async list(
    filter?: TaxSettingFilter | string,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    // Back-compat: the picker used to pass a bare search string.
    const resolved: TaxSettingFilter = typeof filter === 'string' ? { searchKeyword: filter } : filter ?? {};

    return ApiService.executeQuery(LIST_QUERY, {
      filter: resolved,
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async create(params: TaxSettingInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(where: TaxSettingWhereInput, params: TaxSettingInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /** No bulk-remove mutation on the API — fan out and report the first failure. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    const results = await runBulkWrites(ids, (id) => TaxSettingService.remove(id));
    const failed = results.find((r) => r.code !== OP_RESULT_CODES.OK);

    return failed ?? results[results.length - 1] ?? new OpResult();
  }
}
